import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { portalGet } from '../../lib/portalApi';
import { formatISTDateTime } from '../../lib/format';

const STATUS_COLOR = {
  open: 'bg-teal-50 text-[#1F7A6E]',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

function ProgressBar({ delivered, contracted }) {
  const pct = contracted > 0 ? Math.min(100, Math.round((delivered / contracted) * 100)) : 0;
  return (
    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full bg-[#1F7A6E]" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function PortalSalesOrders() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    portalGet('/portal/sales-orders').then((d) => setItems(d.items || [])).catch(() => toast.error('Failed to load your orders'));
  }, []);

  if (items === null) return <div className="text-slate-400 py-10 text-center text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-extrabold text-slate-900">Your Sales Orders</h1>
        <p className="text-sm text-slate-500">What's contracted, delivered, and still to come — per order.</p>
      </div>
      {items.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-sm py-14 text-center text-sm text-slate-400">
          No orders yet. Once ELR confirms your quotation and raises an order, it'll appear here.
        </div>
      )}
      <div className="grid gap-3">
        {items.map((o) => {
          const isOpen = open === o.id;
          return (
            <div key={o.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`portal-so-${o.id}`}>
              <button onClick={() => setOpen(isOpen ? null : o.id)}
                className="w-full text-left p-4 flex items-center justify-between gap-4 hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs text-[#1F7A6E] font-semibold">{o.order_no}</span>
                    <span className={`tag ${STATUS_COLOR[o.status] || ''}`}>{o.status}</span>
                  </div>
                  <div className="font-display font-bold">{o.job_site || 'Site not specified'}</div>
                  <div className="text-xs text-slate-500 mt-1">{formatISTDateTime(o.date)}</div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right w-40">
                    <div className="text-xs text-slate-500 mb-1">{o.total_delivered} / {o.total_contracted} m³ delivered</div>
                    <ProgressBar delivered={o.total_delivered} contracted={o.total_contracted} />
                  </div>
                  {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-300" />}
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-slate-200 overflow-x-auto">
                  <table className="w-full dense-table">
                    <thead><tr>
                      <th>Grade</th><th>Pumping</th><th>Rate ₹/m³</th>
                      <th className="text-right">Contracted</th><th className="text-right">Delivered</th><th className="text-right">Pending</th>
                    </tr></thead>
                    <tbody>
                      {o.lines.map((l) => (
                        <tr key={l.seq}>
                          <td className="font-display font-bold">{l.grade}</td>
                          <td className="text-xs text-slate-500">{l.pumping}</td>
                          <td>{l.rate}</td>
                          <td className="text-right tabular-nums">{l.contracted_qty}</td>
                          <td className="text-right tabular-nums">{l.delivered_qty}</td>
                          <td className="text-right tabular-nums">{l.pending_qty > 0 ? l.pending_qty : <span className="text-emerald-600">done</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
